/* Edited by Jonathan <jonathan@birubi.co> */
/* EV Header behaviour: desktop mega panels and the mobile drill-down drawer.

   Two independent state machines share one element. The desktop side tracks
   which panel is open; the drawer side tracks a stack of pane ids, because
   drilling in is a push and Back is a pop. Both close on Escape and both
   restore focus to whatever opened them. */

/* Scroll thresholds, from the behaviour spec (Figma 1435:2884). They are the
   design's numbers, not invented: ignore a direction change worth less than
   8px, require 40px of cumulative upward scroll before revealing, and do not
   react at all inside the first 120px from the top. */
const CHROME_NOISE = 8;
const CHROME_REVEAL_RUN = 40;
const CHROME_TOP_ZONE = 120;

class EvHeader extends HTMLElement {
  connectedCallback() {
    this.navLinks = Array.from(this.querySelectorAll('[data-ev-panel-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-ev-panel]'));
    this.scrim = this.querySelector('[data-ev-scrim]');
    this.drawer = this.querySelector('[data-ev-drawer]');
    this.panes = Array.from(this.querySelectorAll('[data-ev-pane]'));
    this.burger = this.querySelector('[data-ev-burger]');

    /* Pane ids, root first. The last entry is what the user is looking at. */
    this.paneStack = ['root'];

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.bindDesktop();
    this.bindDrawer();
    this.measureHeaderBottom();
    this.watchHeaderSize();
    this.bindScrollChrome();

    /* A logo that has not decoded yet reports the wrong bar height, which would
       leave the stack's offsets, and the drawer's top, a few pixels out. */
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.measureHeaderBottom(), { once: true });
    }

    document.addEventListener('keydown', this.onKeydown);
  }

  /* Shopify renders header-group sections as siblings with no shared wrapper,
     so "stick the header" means sticking each section at the combined height of
     the ones above it. Done here rather than in CSS because those heights are
     not known up front. The announcement bar can be switched off, and either
     bar can wrap to two lines at narrow widths.

     A bad measurement would be written into inline styles and stay there, so
     anything implausible is discarded and the offsets are simply left alone
     until the ResizeObserver below reports a real height. */
  applyStickyStack() {
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    if (viewportWidth < 200 || viewportHeight < 200) return null;

    const sections = Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
    if (!sections.length) return null;

    /* A header stack taller than a third of the screen is not a header, it is
       a page measured mid-layout. */
    const heights = sections.map((section) => section.getBoundingClientRect().height);
    const total = heights.reduce((sum, height) => sum + height, 0);
    if (total <= 0 || total > viewportHeight / 3) return null;

    let offset = 0;
    sections.forEach((section, index) => {
      section.style.top = `${Math.round(offset)}px`;
      offset += heights[index];
    });

    return total;
  }

  /* The drawer opens below the whole sticky header stack, so it needs that
     stack's total height rather than this section's alone. */
  measureHeaderBottom() {
    /* Written first, and unconditionally: the strip's height is what the
       scroll-up state translates by, and it is still wanted even when the
       stack measurement below is rejected as implausible. */
    this.measureStrip();

    const total = this.applyStickyStack();
    if (!total) return;

    /* Written on the root, not on this element: the drawer reads it, and so
       does the html scroll-padding that keeps in-page anchors from landing
       underneath the now-sticky header. */
    document.documentElement.style.setProperty('--ev-header-bottom', `${Math.round(total)}px`);
  }

  /* Re-measures whenever a bar actually changes height: a merchant editing the
     announcement text, a bar wrapping to two lines, or fonts finishing load,
     rather than trusting one reading taken at connect time. */
  watchHeaderSize() {
    if (typeof ResizeObserver === 'undefined') return;

    this.headerObserver = new ResizeObserver(() => this.measureHeaderBottom());
    document
      .querySelectorAll('.shopify-section-group-header-group')
      .forEach((section) => this.headerObserver.observe(section));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.headerObserver) this.headerObserver.disconnect();
    if (this.onScrollChrome) window.removeEventListener('scroll', this.onScrollChrome);
    if (this.onChromeFocusIn) this.removeEventListener('focusin', this.onChromeFocusIn);
    if (this.onChromeFocusOut) this.removeEventListener('focusout', this.onChromeFocusOut);
    if (this.chromeFrame) cancelAnimationFrame(this.chromeFrame);
    if (this.section) this.section.removeAttribute('data-ev-chrome');
    document.documentElement.classList.remove('ev-header-drawer-open');
  }

  /* ------------------------------------------------------------ desktop */

  bindDesktop() {
    this.navLinks.forEach((link) => {
      const target = link.getAttribute('data-ev-panel-trigger');

      link.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.openPanelId === target) {
          this.closePanel({ focusTrigger: true });
        } else {
          this.openPanel(target);
        }
      });

      /* Hover opens, but only sideways between items once something is
         already open. Otherwise a pointer crossing the bar on its way
         somewhere else would flash panels open. */
      link.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        if (!this.openPanelId) return;
        this.openPanel(target);
      });
    });

    /* Leaving the header entirely closes; moving between the bar and the panel
       does not, because both are inside this element. */
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.closePanel();
    });

    if (this.scrim) {
      this.scrim.addEventListener('click', () => this.closePanel());
    }

    /* A panel left open across a resize would keep an offset measured at the
       old width, so it is re-measured rather than closed. */
    this.onResize = () => {
      this.measureHeaderBottom();
      if (!this.openPanelId) return;
      const panel = this.panels.find(
        (item) => item.getAttribute('data-ev-panel') === this.openPanelId
      );
      if (panel) this.alignPanel(panel, this.openPanelId);
    };
    window.addEventListener('resize', this.onResize);
  }

  openPanel(id) {
    const panel = this.panels.find((item) => item.getAttribute('data-ev-panel') === id);
    if (!panel) return;

    this.panels.forEach((item) => {
      item.toggleAttribute('data-active', item === panel);
    });
    this.alignPanel(panel, id);
    this.navLinks.forEach((link) => {
      link.setAttribute('aria-expanded', link.getAttribute('data-ev-panel-trigger') === id ? 'true' : 'false');
    });

    this.openPanelId = id;
    this.setAttribute('data-open', '');
  }

  /* Inset panels hang under the item that opened them rather than spanning the
     row, so the offset is measured rather than configured, so a merchant
     reordering nav items in the editor never has to re-tune it. The panel is
     held back from the right edge so a wide one shifts left instead of
     overflowing. */
  alignPanel(panel, id) {
    if (!panel.classList.contains('ev-header__panel--inset')) return;

    const trigger = this.navLinks.find((link) => link.getAttribute('data-ev-panel-trigger') === id);
    if (!trigger) return;

    /* Measured against the viewport, not this element: a panel opened before
       ev-header.css has applied would find an unstyled inline element with no
       width and pin every panel to the left gutter. */
    const viewport = document.documentElement.clientWidth;
    if (!viewport) return;

    const sheet = panel.querySelector('.ev-header__sheet');
    const gutter = 60;
    const sheetWidth = sheet ? sheet.offsetWidth : 0;
    const rightLimit = Math.max(gutter, viewport - gutter - sheetWidth);
    const left = trigger.getBoundingClientRect().left;

    panel.style.setProperty(
      '--ev-header-panel-offset',
      `${Math.max(gutter, Math.min(left, rightLimit))}px`
    );
  }

  closePanel(options = {}) {
    if (!this.openPanelId) return;

    const trigger = this.navLinks.find(
      (link) => link.getAttribute('data-ev-panel-trigger') === this.openPanelId
    );

    this.panels.forEach((item) => item.removeAttribute('data-active'));
    this.navLinks.forEach((link) => link.setAttribute('aria-expanded', 'false'));
    this.removeAttribute('data-open');
    this.openPanelId = null;

    if (options.focusTrigger && trigger) trigger.focus();
  }

  /* ------------------------------------------------------------- drawer */

  bindDrawer() {
    if (!this.drawer || !this.burger) return;

    this.burger.addEventListener('click', () => {
      if (this.hasAttribute('data-drawer-open')) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    });

    this.querySelectorAll('[data-ev-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer());
    });

    this.querySelectorAll('[data-ev-pane-push]').forEach((button) => {
      button.addEventListener('click', () => {
        this.pushPane(button.getAttribute('data-ev-pane-push'));
      });
    });

    this.querySelectorAll('[data-ev-pane-pop]').forEach((button) => {
      button.addEventListener('click', () => this.popPane());
    });

    this.renderPanes();
  }

  openDrawer() {
    /* Pin the header BEFORE the drawer is shown. While it is scroll-hidden the
       section carries a transform, and a transformed element is the containing
       block for `position: fixed` descendants -- the drawer would be measured
       against the header instead of the viewport. */
    this.holdChrome(true);
    this.measureHeaderBottom();
    this.setAttribute('data-drawer-open', '');
    this.burger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('ev-header-drawer-open');

    /* Reset to the root each time, so reopening never lands mid-drill. */
    this.paneStack = ['root'];
    this.renderPanes();

    /* Focus moves into the list rather than onto a close button: the burger
       itself is the close control and it stays where it was. */
    const first = this.drawer.querySelector('[data-ev-pane="root"] .ev-header__pane-link');
    if (first) first.focus();

    document.addEventListener('click', this.onDocumentClick);
  }

  closeDrawer() {
    if (!this.hasAttribute('data-drawer-open')) return;

    this.removeAttribute('data-drawer-open');
    this.burger.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('ev-header-drawer-open');
    document.removeEventListener('click', this.onDocumentClick);
    this.holdChrome(false);
    this.burger.focus();
  }

  pushPane(id) {
    if (!id || !this.panes.some((pane) => pane.getAttribute('data-ev-pane') === id)) return;
    this.paneStack.push(id);
    this.renderPanes();
    this.focusPaneStart();
  }

  popPane() {
    if (this.paneStack.length < 2) return;
    const leaving = this.paneStack.pop();
    this.renderPanes();

    /* Return focus to the row that drilled in, not the top of the list. */
    const trigger = this.drawer.querySelector(`[data-ev-pane-push="${leaving}"]`);
    if (trigger) trigger.focus();
  }

  renderPanes() {
    const current = this.paneStack[this.paneStack.length - 1];

    this.panes.forEach((pane) => {
      const id = pane.getAttribute('data-ev-pane');
      const depth = this.paneStack.indexOf(id);

      if (id === current) {
        pane.setAttribute('data-state', 'current');
      } else if (depth > -1) {
        pane.setAttribute('data-state', 'parent');
      } else {
        pane.removeAttribute('data-state');
      }

      /* Off-stack panes must not be tabbable or read out. */
      pane.toggleAttribute('inert', id !== current);
      pane.setAttribute('aria-hidden', id === current ? 'false' : 'true');
    });
  }

  focusPaneStart() {
    const current = this.paneStack[this.paneStack.length - 1];
    const pane = this.panes.find((item) => item.getAttribute('data-ev-pane') === current);
    if (!pane) return;
    const back = pane.querySelector('[data-ev-pane-pop]');
    if (back) back.focus();
  }

  /* ------------------------------------------------------ hide on scroll */

  /* Below 1200 the header hides on the way down and comes back on a deliberate
     scroll up. Only the BAR comes back: the red strip stays away until the user
     is at the very top, so it reads as page furniture rather than a banner that
     follows you down the page. ev-header.css does the moving; this only decides
     when, and writes the strip's height for it to translate by. */
  bindScrollChrome() {
    this.section = this.closest('.ev-header-section');
    if (!this.section) return;

    this.strip = this.querySelector('.ev-header__strip');
    this.chromeState = 'top';
    this.chromeHeld = false;
    this.lastScrollY = Math.max(0, window.scrollY);
    this.upRun = 0;
    this.downRun = 0;
    this.chromeQuery = window.matchMedia('(max-width: 1199px)');

    this.onScrollChrome = this.onScrollChrome.bind(this);
    this.onChromeFocusIn = this.onChromeFocusIn.bind(this);
    this.onChromeFocusOut = this.onChromeFocusOut.bind(this);

    window.addEventListener('scroll', this.onScrollChrome, { passive: true });
    this.addEventListener('focusin', this.onChromeFocusIn);
    this.addEventListener('focusout', this.onChromeFocusOut);

    if (this.chromeQuery.addEventListener) {
      this.chromeQuery.addEventListener('change', () => this.setChrome('top'));
    }

    /* Search is one of Dawn's details-modals. Opening it has to pin the header,
       or the control the panel hangs off can slide away underneath it. */
    const search = this.querySelector('details-modal details');
    if (search) {
      search.addEventListener('toggle', () => this.holdChrome(search.open));
    }

    this.measureStrip();
  }

  measureStrip() {
    if (!this.section) return;
    const strip = this.strip || this.querySelector('.ev-header__strip');
    const height = strip ? Math.round(strip.getBoundingClientRect().height) : 0;
    this.section.style.setProperty('--ev-header-strip-height', `${height}px`);
  }

  onScrollChrome() {
    if (this.chromeFrame) return;
    this.chromeFrame = requestAnimationFrame(() => {
      this.chromeFrame = null;
      this.updateChrome();
    });
  }

  updateChrome() {
    if (!this.section) return;

    const y = Math.max(0, window.scrollY);
    const delta = y - this.lastScrollY;
    this.lastScrollY = y;

    if (!this.chromeQuery.matches) {
      this.setChrome('top');
      return;
    }

    /* Inside the top zone the header is still doing its ordinary job, so
       nothing reacts, and this is the only place the strip comes back. */
    if (y <= CHROME_TOP_ZONE) {
      this.upRun = 0;
      this.downRun = 0;
      this.setChrome('top');
      return;
    }

    /* Accumulated runs rather than a raw direction test: a reversal has to be
       worth more than the noise floor before it counts, so a shaky finger or a
       trackpad's momentum cannot flicker the bar in and out. */
    if (delta > 0) {
      this.downRun += delta;
      if (this.downRun > CHROME_NOISE) this.upRun = 0;
    } else if (delta < 0) {
      this.upRun -= delta;
      if (this.upRun > CHROME_NOISE) this.downRun = 0;
    }

    if (this.downRun > CHROME_NOISE) {
      this.setChrome('hidden');
    } else if (this.upRun >= CHROME_REVEAL_RUN) {
      this.setChrome('bar');
    }
  }

  /* `top` clears the attribute rather than writing a zero translate. ANY
     transform on this element, `will-change` included, would make it the
     containing block for `position: fixed` descendants, and the drill-down
     drawer is one of them. See the matching note in ev-header.css. */
  setChrome(state) {
    if (!this.section) return;

    const next = this.chromeHeld ? 'top' : state;
    if (this.chromeState === next) return;
    this.chromeState = next;

    if (next === 'top') {
      this.section.removeAttribute('data-ev-chrome');
    } else {
      this.section.setAttribute('data-ev-chrome', next);
    }
  }

  /* Held open while the menu or search is open, and while anything inside the
     header holds keyboard focus, so a keyboard user tabbing along the bar can
     never have it slide out from under them. Releasing does not restore the
     hidden state; the next scroll decides. */
  holdChrome(on) {
    this.chromeHeld = !!on;
    if (!this.chromeHeld) return;
    this.upRun = 0;
    this.downRun = 0;
    this.setChrome('top');
  }

  onChromeFocusIn() {
    this.holdChrome(true);
  }

  onChromeFocusOut(event) {
    /* focusout fires on every move inside the header too, so only a focus that
       has actually left it releases the hold. */
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;
    if (this.hasAttribute('data-drawer-open')) return;
    this.holdChrome(false);
  }

  /* -------------------------------------------------------------- global */

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    if (this.hasAttribute('data-drawer-open')) {
      /* Escape steps back out of the drill before it closes the whole drawer,
         which matches how the Back control reads. */
      if (this.paneStack.length > 1) {
        this.popPane();
      } else {
        this.closeDrawer();
      }
      return;
    }

    if (this.openPanelId) this.closePanel({ focusTrigger: true });
  }

  onDocumentClick(event) {
    if (!this.hasAttribute('data-drawer-open')) return;
    if (this.drawer.contains(event.target) || this.burger.contains(event.target)) return;
    this.closeDrawer();
  }
}

if (!customElements.get('ev-header')) {
  customElements.define('ev-header', EvHeader);
}
